import { createEffect, createMemo, createSignal, Match, Show, Switch, useContext } from 'solid-js';
import {
	ActivityIndicator,
	TextLink,
	CheckoutStep,
	Container,
	HorizontalRule,
	Page,
	Heading,
	Button,
	Form,
	HiddenFields,
	Errors,
	Grid,
	GridFive,
	GridSeven,
	TextareaField,
	InputField,
} from '@troon/ui';
import { useParams, useSubmissions } from '@solidjs/router';
import { clientOnly } from '@solidjs/start';
import { Title } from '@solidjs/meta';
import { createStore, produce } from 'solid-js/store';
import dayjs from '@troon/dayjs';
import { IconCalendar } from '@troon/icons/calendar';
import { useUtmParams } from '@troon/primitives/utm';
import { useStripe, StripeSubmit, PaymentElement } from '@troon/payments/stripe';
import { useTrackEvent } from '@troon/analytics';
import { gql, mutationAction, StripeClientSecretIntentType, useMutation } from '../../../../graphql';
import { AccessProductsCtx } from '../../../../providers/card';
import { NotFoundContent } from '../../../../partials/404';
import { usePersisted } from '../../../../providers/persistence-store';
import { ErrorBoundary } from '../../../../components/error-boundary';
import { Receipt } from '../checkout/_components/receipt';
import { ProductInfo } from '../checkout/_components/product-info';
import { DeprecatedPromoCodeEntry } from '../../../../components/promo-code-entry-deprecated';
import { getConfigValue } from '../../../../modules/config';
import type { RouteDefinition } from '@solidjs/router';
import type { StripeError } from '@stripe/stripe-js';

const StripeProvider = clientOnly(async () => ({
	default: (await import('@troon/payments/stripe')).StripeProvider,
}));

export default function GiftCheckout() {
	return (
		<StripeProvider mode="payment" publishableKey={getConfigValue('STRIPE_PUBLISHABLE_KEY')}>
			<GiftCheckoutInternal />
		</StripeProvider>
	);
}

type Steps = 'recipient' | 'sender' | 'payment';
const order: Array<Steps> = ['recipient', 'sender', 'payment'];
const stepFlow = 'Gift';
const stepOptions = { noDuplicates: true };
type Store = {
	current: Steps;
	steps: Record<Steps, { complete: boolean; skipped: boolean }>;
};

function GiftCheckoutInternal() {
	const params = useParams<{ id: string }>();
	const products = useContext(AccessProductsCtx);
	const [errors, setErrors] = createSignal<StripeError | undefined>();
	const [enteredCode, setEnteredCode] = createSignal(false);
	const [persisted, setPersisted] = usePersisted();
	const utm = useUtmParams();
	const [formRef, setFormRef] = createSignal<HTMLFormElement>();
	const trackEvent = useTrackEvent();

	const { setAmount, stripe } = useStripe();

	const productInfo = createMemo(() => {
		return products()?.find((card) => card.id === params.id);
	});

	const createIntent = useMutation(setupIntent);
	// const runCreateIntent = useAction(createIntent);
	const subs = useSubmissions(createIntent);
	const currentSub = createMemo(() => subs[subs.length - 1]);
	const sub = createMemo(
		() => subs.findLast((sub) => !sub.pending && !sub.error && !sub.result?.error) ?? subs[subs.length - 1],
	);

	createEffect(() => {
		if (
			persisted.promoCode?.value &&
			currentSub()?.result?.error?.graphQLErrors?.find((e) => {
				return (
					typeof e.extensions?.displayMessage === 'string' &&
					e.extensions.displayMessage.toLowerCase().includes('promo code')
				);
			})
		) {
			setPersisted(
				produce((s) => {
					delete s.promoCode;
				}),
			);
		}
	});

	createEffect(() => {
		const total = currentSub()?.result?.data?.gift.totalAmount;
		if (total) {
			complete();
			setAmount({ ...total, cents: total.cents! });
		}
	});

	const [store, setStore] = createStore<Store>({
		current: 'recipient',
		steps: {
			recipient: { complete: false, skipped: false },
			sender: { complete: false, skipped: false },
			payment: { complete: false, skipped: false },
		},
	});

	/**
	 * Continues to the next incomplete step in the flow
	 */
	function next() {
		let start = order.indexOf(store.current);
		if (start < 0) {
			return;
		}

		for (; start < order.length - 1; start += 1) {
			const step = order[start]!;
			if (!store.steps[step].complete) {
				setStore('current', step);
				return;
			}
			trackEvent('stepCompleted', { stepFlow, stepName: step, stepSkipped: store.steps[step].skipped }, stepOptions);
		}

		setStore('current', order[order.length - 1]!);
	}

	/**
	 * Completes the current step and auto-advances to the next incomplete step in the flow
	 */
	function complete() {
		setStore(
			produce((s) => {
				s.steps[s.current].complete = true;
			}),
		);
		next();
	}

	/**
	 * Returns to the named step for editing
	 * @param step {Steps}
	 */
	function edit(step: Steps) {
		currentSub()?.clear();
		setStore(
			produce((s) => {
				s.current = step;
				if (step === 'recipient') {
					s.steps.sender.complete = false;
				}
				s.steps[step].complete = false;
			}),
		);
	}

	/**
	 * Get the step's state as a string enum
	 * @param step {Steps}
	 * @returns String enum for step state
	 */
	function stepState(step: Steps) {
		return store.current === step ? 'current' : store.steps[step].complete ? 'completed' : 'waiting';
	}

	function getIndex(step: Steps) {
		// eslint-disable-next-line solid/reactivity
		const index = order.slice(0, order.indexOf(step)).reduce((index, step) => {
			return index + (!store.steps[step].skipped ? 1 : 0);
		}, 1);
		return index;
	}

	const data = createMemo(() => {
		const ref = formRef();
		if (store.current && ref) {
			return new FormData(ref);
		}
		return new FormData();
	});

	return (
		<div class="-mb-6 min-h-svh bg-neutral-100 md:-mb-12">
			<Page>
				<Show when={productInfo()} fallback={<NotFoundContent />}>
					{(productInfo) => (
						<>
							<Title>Checkout | Gift Access Premium | Golf with Access</Title>
							<div class="-mt-8 -mb-6 md:-mb-12 lg:hidden">
								<ProductInfo product={productInfo()} isGift />
							</div>
							<Container>
								<Heading as="h1" size="h2" class="sr-only md:not-sr-only">
									Gift Access Premium
								</Heading>
								<Grid>
									<GridSeven class="flex flex-col gap-4">
										<Form ref={setFormRef} action={createIntent} document={query} class="flex flex-col gap-4">
											<HiddenFields
												data={{
													utm: utm(),
													id: sub()?.result?.data?.gift.id,
													productId: sub()?.result?.data?.gift.id ? undefined : params.id,
													promoCode: currentSub()?.result?.data?.gift.promoCode,
												}}
											/>

											<CheckoutStep
												state={stepState('recipient')}
												step={getIndex('recipient')}
												title="Gift recipient details"
												onEdit={() => edit('recipient')}
												value={
													<div class="flex flex-col gap-3 overflow-hidden">
														<p class="overflow-ellipsis">
															<b class="font-medium">{data().get('giftParams.recipientName') as string}</b>
															<br />
															{data().get('giftParams.recipientEmail') as string}
														</p>
														<HorizontalRule />
														<p class="overflow-ellipsis">
															{(data().get('giftParams.message') as string) ?? 'No message'}
														</p>
														<HorizontalRule />
														<p class="overflow-ellipsis">
															<IconCalendar class="text-brand" /> Send on{' '}
															{dayjs(data().get('notificationDate') as string).format('MMMM D, YYYY')}
														</p>
													</div>
												}
											>
												<InputField
													name="giftParams.recipientName"
													label="Recipient’s Name"
													autocomplete="off"
													maxLength={100}
												/>

												<InputField
													name="giftParams.recipientEmail"
													label="Recipient’s Email Address"
													inputMode="email"
													type="email"
													autocomplete="off"
													maxLength={100}
												/>

												<TextareaField
													name="giftParams.message"
													label="Gift Message"
													autocomplete="off"
													maxLength={255}
												/>

												<InputField
													name="notificationDate"
													label="Email Notification"
													description="Your recipient will be notified immediately about their gift. If you'd like to delay the notification, please enter your preferred date below."
													type="date"
													min={dayjs().format('YYYY-MM-DD')}
													max={dayjs().add(3, 'month').format('YYYY-MM-DD')}
													placeholder="Send immediately"
													value={dayjs().format('YYYY-MM-DD')}
												/>

												<Errors />

												<Button class="size-fit" type="button" onClick={() => complete()}>
													Continue
												</Button>
											</CheckoutStep>

											<CheckoutStep
												state={stepState('sender')}
												step={getIndex('sender')}
												title="Your details"
												onEdit={() => edit('sender')}
												value={
													<div class="flex flex-col gap-3 overflow-hidden">
														<p class="overflow-ellipsis">
															<b class="font-medium">{data().get('giftParams.purchaserName') as string}</b>
															<br />
															{data().get('giftParams.purchaserEmail') as string}
														</p>
													</div>
												}
											>
												<InputField name="giftParams.purchaserName" label="Name" autocomplete="off" maxLength={100} />

												<InputField
													name="giftParams.purchaserEmail"
													label="Email Address"
													inputMode="email"
													type="email"
													autocomplete="email"
													maxLength={100}
												/>

												<Errors />

												<Button class="size-fit" type="submit">
													Continue
												</Button>
											</CheckoutStep>
										</Form>

										<CheckoutStep state={stepState('payment')} step={getIndex('payment')} title="Payment information">
											<ErrorBoundary>
												<Show when={stripe.state !== 'errored'}>
													<div>
														<PaymentElement />
													</div>
												</Show>
											</ErrorBoundary>
										</CheckoutStep>
									</GridSeven>

									<GridFive>
										<div class="overflow-y-hidden border-neutral lg:rounded-lg lg:border lg:bg-white">
											<div class="hidden overflow-hidden lg:block">
												<ProductInfo product={productInfo()} isGift />
											</div>

											<Show when={sub()?.result}>
												<div data-testId="receipt" class="flex flex-col gap-6 py-6 lg:p-6">
													<Switch fallback={<ActivityIndicator />}>
														{/* Important: don't show errors here. They'll show inline instead */}
														<Match when={sub()?.result?.error?.graphQLErrors}>{null}</Match>
														<Match when={sub()?.result?.data?.gift}>
															{(gift) => (
																<>
																	<DeprecatedPromoCodeEntry
																		action={createIntent}
																		// @ts-expect-error TODO not sure why this is complaining
																		document={query}
																		promoCode={gift().promoCode as string | undefined}
																		intentData={{
																			id: gift().id,
																			giftParams: {
																				purchaserName: gift().purchaserName,
																				purchaserEmail: gift().purchaserEmail,
																				recipientName: gift().recipientName,
																				recipientEmail: gift().recipientEmail,
																				message: gift().message,
																				deliverAt: gift().deliverAt,
																			},
																		}}
																		onUpdatePromoCode={(code) => setEnteredCode(!!code)}
																	/>
																	<Receipt
																		discount={gift().discountAmount}
																		subtotal={gift().subtotalAmount}
																		tax={gift().taxAmount}
																		total={gift().totalAmount}
																		promoCode={gift().promoCode}
																	/>
																</>
															)}
														</Match>
													</Switch>

													<HorizontalRule />

													<StripeSubmit
														productId={params.id!}
														redirectPath="/membership/gift/confirm"
														disabled={
															(enteredCode() && !currentSub()?.result?.data?.gift.promoCode) ||
															currentSub()?.pending ||
															!!currentSub()?.result?.error
														}
														onError={(errors) => setErrors(errors)}
														stripeClientSecret={currentSub()?.result?.data?.gift.stripeClientSecret}
														stripeClientSecretIntentType={StripeClientSecretIntentType.Payment}
														troonCardSubscriptionId={currentSub()?.result?.data?.gift.id}
													>
														Purchase Gift
													</StripeSubmit>

													<Show when={errors()}>
														{(error) => (
															<p class="text-red-500" aria-live="assertive">
																There was a problem completing the request: {error().message}
															</p>
														)}
													</Show>

													<p class="text-center text-sm">
														By clicking “Purchase Gift”, you are agreeing to Troon’s{' '}
														<TextLink target="_blank" href="/terms">
															Terms of Service
														</TextLink>{' '}
														and{' '}
														<TextLink target="_blank" href="/privacy-policy">
															Privacy Policy
														</TextLink>
														.
													</p>
												</div>
											</Show>
										</div>
									</GridFive>
								</Grid>
							</Container>
						</>
					)}
				</Show>
			</Page>
		</div>
	);
}

const query = gql(`mutation createOrUpdateAccessGiftPurchase(
  $productId: String
  $id: String
	$giftParams: AccessGiftPurchaseInput!
) {
  gift: createOrUpdateAccessGiftPurchaseIntent(
    troonCardSubscriptionProductId: $productId
		id: $id
    giftParams: $giftParams
  ) {
  	id
  	status
  	purchaserName
  	purchaserEmail
  	recipientName
  	recipientEmail
  	message
  	promoCode
  	stripeClientSecret
   	totalAmount { displayValue, value, code, cents }
		subtotalAmount { displayValue, value }
		discountAmount { displayValue, value }
		taxAmount { displayValue, value }
  	deliverAt
  }
}`);

const setupIntent = mutationAction(query, {
	transform: (data) => {
		const notificationDate = data.get('notificationDate') as string;
		const ret = { giftParams: { promoCode: (data.get('promoCode') as string | undefined) || undefined } };
		if (notificationDate) {
			// @ts-ignore okay for now
			ret.giftParams.deliverAt = dayjs.tz(notificationDate, 'America/Phoenix').set('hour', 9).toISOString();
		}
		return ret;
	},
});

export const route = {
	info: { nav: { appearance: 'extra-minimal', sticky: false }, banner: { hide: true } },
} satisfies RouteDefinition;
